import { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import {
  CreditCard,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Download,
  Calendar,
  Phone,
  MessageSquare,
  ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-frontend/components/ui/table';
import { Progress } from '@easyflow/ui-frontend/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@easyflow/ui-frontend/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '../../lib/utils';


interface UsageRecord {
  category: string;
  description: string;
  usage: number;
  usage_unit: string;
  count: number;
  price: number;
  price_unit: string;
}

interface NumberBreakdown {
  phone_number: string;
  channel: string;
  message_count: number;
  sent: number;
  received: number;
}

interface BillingData {
  usage_records: UsageRecord[];
  number_breakdown?: NumberBreakdown[];
  total_cost: number;
  currency: string;
  period: {
    start: string;
    end: string;
  };
  configured: boolean;
  twilio_error?: boolean;
}

interface PhoneNumber {
  id: string;
  phone_number: string;
  type: string;
  label?: string;
}

interface TemplateName {
  name: string;
  category: string;
  status: string;
}

export default function MessagingBilling() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [templateNames, setTemplateNames] = useState<TemplateName[]>([]);
  
  // Filtri gerarchici
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [selectedWhatsAppType, setSelectedWhatsAppType] = useState<string>('all'); // all / template / conversation
  const [selectedTemplate, setSelectedTemplate] = useState<string>('all'); // nome template specifico
  const [selectedNumber, setSelectedNumber] = useState<string>('all');
  
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const [startDate, setStartDate] = useState<string>(firstDayOfMonth.toISOString().split('T')[0]!);
  const [endDate, setEndDate] = useState<string>(now.toISOString().split('T')[0]!);
  
  const loadPhoneNumbers = async () => {
    try {
      const response = await apiClient.get<{ senders: PhoneNumber[] }>('/messaging/senders');
      if (response.success && response.data?.senders) {
        setPhoneNumbers(response.data.senders);
      }
    } catch (error) {
      console.error('Errore caricamento numeri:', error);
    }
  };
  
  const loadTemplateNames = async () => {
    try {
      const response = await apiClient.get<TemplateName[]>('/messaging/templates/all-names');
      if (response.success && response.data) {
        setTemplateNames(response.data);
      }
    } catch (error) {
      console.error('Errore caricamento template:', error);
    }
  };

  const loadBilling = useCallback(async () => {
    try {
      setLoading(true);
      
      const url = `/messaging/billing/usage?start_date=${startDate}&end_date=${endDate}`;
      
      const response = await apiClient.get<BillingData>(url);
      if (response.success && response.data) {
        setBilling(response.data);
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile caricare i dati di billing',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [endDate, startDate, toast]);

  useEffect(() => {
    loadBilling();
    loadPhoneNumbers();
    loadTemplateNames();
  }, [startDate, endDate, loadBilling]);
  
  // Reset filtri dipendenti quando cambia il canale
  useEffect(() => {
    setSelectedWhatsAppType('all');
    setSelectedTemplate('all');
    setSelectedNumber('all');
  }, [selectedChannel]);
  
  // Reset template quando cambia il tipo WhatsApp
  useEffect(() => {
    setSelectedTemplate('all');
  }, [selectedWhatsAppType]);
  
  // Numeri filtrati per canale selezionato
  const filteredPhoneNumbers = phoneNumbers.filter(pn => {
    if (selectedChannel === 'all') return true;
    if (selectedChannel === 'whatsapp') return pn.type === 'whatsapp' || pn.type === 'both';
    if (selectedChannel === 'sms' || selectedChannel === 'mms') return pn.type === 'sms' || pn.type === 'both';
    return true;
  });
  
  const filteredRecords = billing?.usage_records.filter(record => {
    const cat = record.category.toLowerCase();
    const desc = (record.description || '').toLowerCase();
    
    // Filtro 1: Canale
    if (selectedChannel !== 'all') {
      const matchesChannel = cat.includes(selectedChannel.toLowerCase()) || desc.includes(selectedChannel.toLowerCase());
      if (!matchesChannel) return false;
    }
    
    // Filtro 2: Tipo WhatsApp (solo se canale = whatsapp)
    if (selectedChannel === 'whatsapp' && selectedWhatsAppType !== 'all') {
      if (selectedWhatsAppType === 'template') {
        // Template = marketing, utility, authentication
        const isTemplate = desc.includes('marketing') || desc.includes('utility') || 
                          desc.includes('authentication') || desc.includes('template') ||
                          cat.includes('marketing') || cat.includes('utility') || cat.includes('authentication');
        if (!isTemplate) return false;
      } else if (selectedWhatsAppType === 'conversation') {
        // Conversazione = service, user-initiated
        const isConversation = desc.includes('service') || desc.includes('user-initiated') || 
                               desc.includes('conversation') || cat.includes('service');
        if (!isConversation) return false;
      }
    }
    
    // Filtro 3: Template specifico (solo se tipo = template)
    if (selectedChannel === 'whatsapp' && selectedWhatsAppType === 'template' && selectedTemplate !== 'all') {
      const templateName = selectedTemplate.toLowerCase();
      // Il nome del template potrebbe essere nella description o category
      if (!desc.includes(templateName) && !cat.includes(templateName)) {
        return false;
      }
    }
    
    // Filtro 4: Numero specifico (confronto con description se contiene il numero)
    if (selectedNumber !== 'all') {
      const normalizedNumber = selectedNumber.replace(/[^\d+]/g, '');
      if (!desc.includes(normalizedNumber) && !cat.includes(normalizedNumber)) {
        // Non possiamo filtrare per numero direttamente dai records Twilio
        // ma teniamo il filtro per future implementazioni
      }
    }
    
    return true;
  }) || [];
  
  const filteredTotalCost = filteredRecords.reduce((sum, r) => sum + r.price, 0);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getCategoryIcon = (category: string) => {
    if (category.includes('sms') || category.includes('message')) {
      return <MessageSquare className="h-4 w-4" />;
    }
    if (category.includes('call') || category.includes('voice')) {
      return <Phone className="h-4 w-4" />;
    }
    return <DollarSign className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!billing) {
    return (
      <div className="p-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Configurazione richiesta</AlertTitle>
          <AlertDescription>
            Per visualizzare i dati di billing, configura prima le credenziali Twilio nella sezione Impostazioni.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Billing Twilio</h1>
          <p className="text-muted-foreground">Monitoraggio costi e utilizzo</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadBilling}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Esporta
          </Button>
        </div>
      </div>
      
      {billing.twilio_error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Dati di costo Twilio non disponibili</AlertTitle>
          <AlertDescription>
            Non è stato possibile recuperare i costi da Twilio in questo momento. Il dettaglio dei messaggi per numero resta consultabile; i costi e le categorie di utilizzo potrebbero non essere aggiornati. Riprova più tardi o verifica le credenziali Twilio.
          </AlertDescription>
        </Alert>
      )}

      {/* Filtri Gerarchici */}
      <Card className="bg-gray-50/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Filtro 1: Date */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Periodo:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-1.5 border rounded-md text-sm bg-background"
              />
              <span className="text-muted-foreground">→</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-1.5 border rounded-md text-sm bg-background"
              />
            </div>
            
            <div className="h-6 w-px bg-border" />
            
            {/* Filtro 2: Canale */}
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Canale:</span>
              <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="mms">MMS</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Filtro 3: Tipo WhatsApp (solo se canale = whatsapp) */}
            {selectedChannel === 'whatsapp' && (
              <>
                <div className="h-6 w-px bg-border" />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Tipo:</span>
                  <Select value={selectedWhatsAppType} onValueChange={setSelectedWhatsAppType}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Tutti i tipi" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i tipi</SelectItem>
                      <SelectItem value="template">Template (a pagamento)</SelectItem>
                      <SelectItem value="conversation">Conversazione (gratis 24h)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {/* Filtro 3b: Template specifico (solo se tipo = template) */}
            {selectedChannel === 'whatsapp' && selectedWhatsAppType === 'template' && templateNames.length > 0 && (
              <>
                <div className="h-6 w-px bg-border" />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Template:</span>
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Tutti i template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i template</SelectItem>
                      {templateNames.map(t => (
                        <SelectItem key={t.name} value={t.name}>
                          <div className="flex items-center gap-2">
                            <span>{t.name}</span>
                            <Badge variant="outline" className="text-[10px] px-1 py-0">
                              {t.category}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {/* Filtro 4: Numeri (filtrato per canale) */}
            {filteredPhoneNumbers.length > 0 && (
              <>
                <div className="h-6 w-px bg-border" />
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Numero:</span>
                  <Select value={selectedNumber} onValueChange={setSelectedNumber}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Tutti i numeri" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i numeri</SelectItem>
                      {filteredPhoneNumbers.map(pn => (
                        <SelectItem key={pn.id} value={pn.phone_number}>
                          {pn.phone_number} {pn.label ? `(${pn.label})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          
          {/* Riepilogo filtri attivi */}
          {(selectedChannel !== 'all' || selectedWhatsAppType !== 'all' || selectedTemplate !== 'all' || selectedNumber !== 'all') && (
            <div className="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Filtri attivi:</span>
              {selectedChannel !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  {selectedChannel.toUpperCase()}
                </Badge>
              )}
              {selectedChannel === 'whatsapp' && selectedWhatsAppType !== 'all' && (
                <Badge variant="outline" className="text-xs">
                  {selectedWhatsAppType === 'template' ? 'Solo Template' : 'Solo Conversazioni'}
                </Badge>
              )}
              {selectedTemplate !== 'all' && (
                <Badge variant="default" className="text-xs bg-purple-600">
                  Template: {selectedTemplate}
                </Badge>
              )}
              {selectedNumber !== 'all' && (
                <Badge variant="outline" className="text-xs">
                  {selectedNumber}
                </Badge>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-xs"
                onClick={() => {
                  setSelectedChannel('all');
                  setSelectedWhatsAppType('all');
                  setSelectedTemplate('all');
                  setSelectedNumber('all');
                }}
              >
                Pulisci filtri
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Costo Totale {selectedChannel !== 'all' ? selectedChannel.toUpperCase() : 'Periodo'}</p>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold">{formatCurrency(filteredTotalCost, billing.currency)}</p>
            <p className="text-sm text-muted-foreground mt-2">
              Dal {format(new Date(startDate), 'dd/MM/yyyy')} al {format(new Date(endDate), 'dd/MM/yyyy')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Categorie Filtrate</p>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold">{filteredRecords.length}</p>
            <p className="text-sm text-muted-foreground mt-2">Servizi {selectedChannel !== 'all' ? selectedChannel.toUpperCase() : 'utilizzati'}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dettaglio Utilizzo</CardTitle>
          <CardDescription>Breakdown per categoria di servizio</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Categoria</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead className="text-right">Utilizzo</TableHead>
                <TableHead className="text-right">Conteggio</TableHead>
                <TableHead className="text-right">Costo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    Nessun utilizzo nel periodo selezionato
                  </TableCell>
                </TableRow>
              ) : (
                filteredRecords.map((record, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(record.category)}
                        <span className="font-medium">{record.category}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{record.description}</TableCell>
                    <TableCell className="text-right">
                      {record.usage} {record.usage_unit}
                    </TableCell>
                    <TableCell className="text-right">{record.count.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(record.price, record.price_unit)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Dettaglio per Numero</CardTitle>
          <CardDescription>
            Conteggio messaggi per numero e canale dai dati interni
            {billing.twilio_error ? ' (sempre disponibile, anche senza i costi Twilio)' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numero</TableHead>
                <TableHead>Canale</TableHead>
                <TableHead className="text-right">Totale</TableHead>
                <TableHead className="text-right">Inviati</TableHead>
                <TableHead className="text-right">Ricevuti</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!billing.number_breakdown || billing.number_breakdown.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    Nessun messaggio nel periodo selezionato
                  </TableCell>
                </TableRow>
              ) : (
                billing.number_breakdown.map((row, idx) => (
                  <TableRow key={`${row.phone_number}-${row.channel}-${idx}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{row.phone_number}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="uppercase text-xs">{row.channel}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">{row.message_count.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{row.sent.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{row.received.toLocaleString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-blue-50/50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <ExternalLink className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900">Console Twilio</h4>
              <p className="text-sm text-blue-700 mt-1">
                Per dettagli completi su fatturazione, storico pagamenti e impostazioni account, 
                visita la <a href="https://console.twilio.com/us1/billing/manage-billing" target="_blank" rel="noopener noreferrer" className="underline font-medium">Console Twilio</a>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
